/* eslint-disable max-classes-per-file */
/* eslint-disable no-bitwise */
import { JsonPipe, NgClass, NgTemplateOutlet } from "@angular/common";
import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BehaviorSubject } from "rxjs";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";

import { ValidationContext } from "~app/common/validation/validationContext";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { FormMode } from "~app/features/forsaljning/produkt/produkt-detail/produkt-details.models";
import { LineBreakPipe } from "../../../../pipes/line-break.pipe";
import { ButtonComponent } from "../../../button/button.component";
import { InputNumberComponent } from "../../../input-number/input-number.component";
import { MessagePanelComponent } from "../../../message-panel/message-panel.component";
import { RadiobuttonGroupComponent } from "../../../radiobuttongroup/radiobuttongroup.component";
import { SimpleSelectorComponent } from "../../../simple-selector/simple-selector.component";
@Component({
    selector: "rask-prompt-dialog",
    templateUrl: "./prompt-dialog.component.html",
    styleUrls: ["./prompt-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MessagePanelComponent,
        FormsModule,
        ReactiveFormsModule,
        NgClass,
        NgTemplateOutlet,
        ButtonComponent,
        InputNumberComponent,
        RadiobuttonGroupComponent,
        SimpleSelectorComponent,
        JsonPipe,
        LineBreakPipe
    ]
})
export class PromptDialogComponent implements OnInit {
    modal = inject(NgbActiveModal);

    formMode = FormMode.NotSet;
    form = new UntypedFormGroup({});
    input: PromptInput<any>;
    fieldData: InputFieldInternal[];
    validationContext: ValidationContext;
    validationItems = new BehaviorSubject<IMessageModel[]>([]);
    formValid: boolean;

    ngOnInit(): void {
        this.fieldData = Object
            .keys(this.input.fields)
            .map(c => ({
                field: this.input.fields[c],
                key: c
            })).map(c => {
                const field = new InputFieldInternal();
                field.id = c.key;
                field.label = c.field.label;
                field.value = c.field.value;
                field.type = c.field.type;
                field.values = c.field.values;
                field.options = c.field.options;
                field.validators = c.field.validators;
                field.columnClass = c.field.columnClass;
                return field;
            });
        this.validationContext = new ValidationContext();
        const rules = [];

        this.fieldData.filter(p => !!p.validators)
            .forEach((field, index) => {
                field.validators.forEach(validator => {
                    rules.push({
                        id: index + 1,
                        errorLevel: ValidationErrorLevels.error,
                        expression: validator.expression,
                        message: { text: validator.message },
                        model: () => field.value,
                        name: field.id
                    });
                });
            });

        this.validationContext.create("default").withRules(rules);
        this.fieldData.forEach(x => {
            x.rules = this.validationContext.current[x.id];
        });
    }

    dismiss(): void {
        const result = new PromptResponse({});
        result.cancel = true;
        this.modal.close(result);
    }

    get deleteText(): string {
        return "Ta bort";
    }

    get saveText(): string {
        return this.input.mode === InputMode.isNew ? this.input.addText : this.input.editText;
    }

    get description(): string {
        return this.input.description;
    }

    save(): void {
        if (this.canSave()) {
            const result = new PromptResponse({});
            result.action = this.input.mode === InputMode.isNew ? Action.add : Action.edit;
            result.cancel = false;
            this.fieldData.forEach(c => {
                const field = new FieldValue();
                field.value = c.value;
                result.fields[c.id] = field;
            });

            this.modal.close(result);
        }
    }

    canDelete(): boolean {
        return (this.input.mode & InputMode.canDelete) !== 0 && (this.input.mode & InputMode.readOnly) === 0;
    }

    delete(): void {
        this.modal.close(({
            action: Action.delete
        }) as PromptResponse<any>);
    }

    canSave(): boolean {
        return this.form.dirty && this.formValid && !this.validationItems.value.some(x => x.severity === MessageSeverity.Error) && (this.input.mode & InputMode.readOnly) === 0;
    }

    valueChanges() {
        this.form.markAsDirty();
        this.formValid = this.validationContext.validate();
        if (this.input.onValidate) {
            this.validationItems.next(this.input.onValidate(this.fieldData));
        }
    }

}

export class PromptResponse<T extends IFieldDecleration> {
    action: Action;
    cancel: boolean;
    readonly fields: T;
    constructor(fields: T) {
        this.fields = fields;
    }
}

export interface IFieldDecleration {
    [key: string]: FieldValue;
}

export class PromptInput<T extends IFieldDecleration> {
    mode: InputMode;
    addText = "Lägg till";
    editText = "Ändra";
    description: string;
    onValidate?: (fields: InputField[]) => IMessageModel[];
    readonly fields: T;

    constructor(mode: InputMode, fields: T) {
        this.mode = mode;
        this.fields = fields;
    }
}
export interface IPromptInput {
    [fieldId: string]: any | InputField;
}
export class TypedFieldValue<T> {
    [others: string]: any;
    value: T;
}

export class FieldValue extends TypedFieldValue<any> {
    [others: string]: any;
    value: any;
    type: FieldType;
}

export class InputField extends FieldValue {
    label: string;
    value: any;
    type: FieldType;
    columnClass: string;
    validators?: IValidation[];
    options?: any[];
}

type FieldType = "number" | "button-group" | "simple-selector" | "text" | "json";

class InputFieldInternal extends InputField {
    id: string;
    rules: any;
}

export interface IValidation {
    message: string;
    expression: (value: any, oldval?: any) => boolean;
}

export enum InputMode {
    none = 0,
    isNew = 1 << 0,
    canDelete = 1 << 1,
    readOnly = 1 << 2
}

export enum Action {
    none = 0,
    add = 1,
    edit = 2,
    delete = 3
}
